import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { leaveSlotService, LeaveSlot } from '../../../services/availability/leaveSlotService';
import { DoctorPractice } from '../../../services/profile/profileService';
import AvailabilityCalendar from '../AvailabilityCalendar/AvailabilityCalendar';
import AddLeaveForm from '../AddLeaveForm/AddLeaveForm';
import LeaveDetailsView from '../LeaveDetailsView/LeaveDetailsView';
import EditLeaveForm from '../EditLeaveForm/EditLeaveForm';
import './EditAvailabilityContent.css';

type LeaveStatus = 'active' | 'cancelled' | 'expired' | 'pending_confirmation' | null;

interface EditAvailabilityContentProps {
  practices: DoctorPractice[];
}

const EditAvailabilityContent: React.FC<EditAvailabilityContentProps> = ({ practices }) => {
  const [leaveSlots, setLeaveSlots] = useState<LeaveSlot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [displayedMonth, setDisplayedMonth] = useState<Date>(new Date());
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<LeaveStatus>(null);
  const [appointmentCounts, setAppointmentCounts] = useState<Record<string, number>>({});
  const [isLoadingAppointmentCounts, setIsLoadingAppointmentCounts] = useState<boolean>(false);
  const [showingAddLeave, setShowingAddLeave] = useState<boolean>(false);
  const [selectedLeaveSlot, setSelectedLeaveSlot] = useState<LeaveSlot | null>(null);
  const [editingLeaveSlot, setEditingLeaveSlot] = useState<LeaveSlot | null>(null);
  const [deletingLeaveSlotId, setDeletingLeaveSlotId] = useState<number | null>(null);
  const [showingDeleteConfirm, setShowingDeleteConfirm] = useState<boolean>(false);

  const formatDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const loadLeaveSlots = useCallback(async () => {
    try {
      const today = new Date();
      const endDate = new Date();
      endDate.setDate(today.getDate() + 90);

      const startDateStr = formatDateString(today);
      const endDateStr = formatDateString(endDate);
      const statusStr = selectedStatusFilter || undefined;

      const slots = await leaveSlotService.getLeaveSlots({
        start_date: startDateStr,
        end_date: endDateStr,
        status: statusStr,
      });

      setLeaveSlots(slots);
    } catch (err: any) {
      console.error('Failed to load leave slots:', err);
    }
  }, [selectedStatusFilter]);

  const loadAppointmentCountsForMonth = useCallback(async (month: Date) => {
    try {
      setIsLoadingAppointmentCounts(true);
      const calendar = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthStart = new Date(calendar);
      const monthEnd = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);

      const startDateStr = formatDateString(monthStart);
      const endDateStr = formatDateString(monthEnd);

      const counts = await leaveSlotService.getAppointmentCounts(startDateStr, endDateStr);
      setAppointmentCounts((prev) => ({ ...prev, ...counts }));
    } catch (err: any) {
      console.error('Failed to load appointment counts:', err);
    } finally {
      setIsLoadingAppointmentCounts(false);
    }
  }, []);

  useEffect(() => {
    loadLeaveSlots();
  }, [loadLeaveSlots]);

  useEffect(() => {
    loadAppointmentCountsForMonth(displayedMonth);
  }, [displayedMonth, loadAppointmentCountsForMonth]);

  const leaveSlotsForDate = (date: Date): LeaveSlot[] => {
    const dateStr = formatDateString(date);
    return leaveSlots.filter((slot) => slot.leave_date === dateStr);
  };

  const hasFullDayLeave = (date: Date): boolean => {
    const slots = leaveSlotsForDate(date).filter(
      (slot) => slot.status.toLowerCase() !== 'cancelled'
    );
    return (
      slots.some((slot) => slot.start_time === '00:00' && slot.end_time === '23:59') ||
      slots.reduce((total, slot) => {
        const duration = durationInHours(slot.start_time, slot.end_time);
        return total + duration;
      }, 0) >= 8.0
    );
  };

  const hasPartialLeave = (date: Date): boolean => {
    const slots = leaveSlotsForDate(date).filter(
      (slot) => slot.status.toLowerCase() !== 'cancelled'
    );
    return slots.length > 0 && !hasFullDayLeave(date);
  };

  const durationInHours = (start: string, end: string): number => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    return (endMinutes - startMinutes) / 60;
  };

  // Days of the week where at least one practice has timings configured (is_available)
  const practiceDayNames = useMemo(() => {
    const set = new Set<string>();
    practices.forEach((practice) => {
      practice.timings?.forEach((t) => {
        if (t.is_available && t.day_of_week) {
          set.add(t.day_of_week.toLowerCase());
        }
      });
    });
    return set;
  }, [practices]);

  const isDateEnabled = (date: Date): boolean => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const normalizedDate = new Date(date);
    normalizedDate.setHours(0, 0, 0, 0);

    if (normalizedDate < today) {
      return false;
    }

    if (practices.length === 0) {
      return false;
    }

    // If no practice days are configured, allow all future dates (fallback)
    if (practiceDayNames.size === 0) {
      return true;
    }

    // Enable only dates that fall on a configured practice day of the week
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[normalizedDate.getDay()];
    return practiceDayNames.has(dayName);
  };

  const handleMonthChange = (month: Date) => {
    setDisplayedMonth(month);
    loadLeaveSlotsForMonth(month);
  };

  const loadLeaveSlotsForMonth = async (month: Date) => {
    try {
      const calendar = new Date(month.getFullYear(), month.getMonth(), 1);
      const monthStart = new Date(calendar);
      const monthEnd = new Date(calendar.getFullYear(), calendar.getMonth() + 1, 0);

      const startDateStr = formatDateString(monthStart);
      const endDateStr = formatDateString(monthEnd);
      const statusStr = selectedStatusFilter || undefined;

      const slots = await leaveSlotService.getLeaveSlots({
        start_date: startDateStr,
        end_date: endDateStr,
        status: statusStr,
      });

      const monthStr = `${month.getFullYear()}-${String(month.getMonth() + 1).padStart(2, '0')}`;
      setLeaveSlots((prev) => {
        const filtered = prev.filter((slot) => {
          const slotDate = new Date(slot.leave_date);
          const slotMonthStr = `${slotDate.getFullYear()}-${String(slotDate.getMonth() + 1).padStart(2, '0')}`;
          return slotMonthStr !== monthStr;
        });
        return [...filtered, ...slots];
      });
    } catch (err: any) {
      console.error('Failed to load leave slots for month:', err);
    }
  };

  const slotsForSelectedDate = leaveSlotsForDate(selectedDate);

  const formatTime = (time: string): string => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatLeaveType = (type: string): string => {
    const typeMap: Record<string, string> = {
      holiday: 'Holiday',
      sick_leave: 'Sick Leave',
      emergency: 'Emergency',
      other: 'Other',
    };
    return typeMap[type] || type;
  };

  const formatLeaveScope = (scope: string): string => {
    return scope === 'all_practices' ? 'All Practices' : 'Single Practice';
  };

  const handleEditLeave = (slot: LeaveSlot, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingLeaveSlot(slot);
  };

  const handleDeleteLeave = (slot: LeaveSlot, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingLeaveSlotId(slot.id);
    setShowingDeleteConfirm(true);
  };

  const confirmDeleteLeave = async () => {
    if (!deletingLeaveSlotId) return;

    try {
      await leaveSlotService.cancelLeaveSlot(deletingLeaveSlotId, true);
      setShowingDeleteConfirm(false);
      setDeletingLeaveSlotId(null);
      loadLeaveSlots();
      loadAppointmentCountsForMonth(displayedMonth);
    } catch (err: any) {
      console.error('Failed to delete leave:', err);
      alert(err.response?.data?.detail || err.message || 'Failed to delete leave');
    }
  };

  return (
    <div className="edit-availability-content-wrapper">
      {/* Status Filter Chips */}
      <div className="status-filter-chips">
        <button
          type="button"
          className={`status-filter-chip ${selectedStatusFilter === null ? 'active' : ''}`}
          onClick={() => setSelectedStatusFilter(null)}
        >
          All
        </button>
        <button
          type="button"
          className={`status-filter-chip ${selectedStatusFilter === 'active' ? 'active' : ''}`}
          onClick={() => setSelectedStatusFilter('active')}
        >
          Active
        </button>
        <button
          type="button"
          className={`status-filter-chip ${selectedStatusFilter === 'pending_confirmation' ? 'active' : ''}`}
          onClick={() => setSelectedStatusFilter('pending_confirmation')}
        >
          Pending
        </button>
        <button
          type="button"
          className={`status-filter-chip ${selectedStatusFilter === 'cancelled' ? 'active' : ''}`}
          onClick={() => setSelectedStatusFilter('cancelled')}
        >
          Cancelled
        </button>
      </div>

      {/* Calendar */}
      <AvailabilityCalendar
        selectedDate={selectedDate}
        displayedMonth={displayedMonth}
        leaveSlots={leaveSlots}
        appointmentCounts={appointmentCounts}
        onDateSelect={setSelectedDate}
        onMonthChange={handleMonthChange}
        hasFullDayLeave={hasFullDayLeave}
        hasPartialLeave={hasPartialLeave}
        isDateEnabled={isDateEnabled}
        isLoadingAppointmentCounts={isLoadingAppointmentCounts}
      />
      {practiceDayNames.size > 0 && (
        <p className="calendar-practice-days-hint">
          Only days when you have practice timings are selectable.
        </p>
      )}

      {/* Add Leave Button */}
      <div className="add-leave-button-container">
        <button
          type="button"
          className="add-leave-button"
          onClick={() => setShowingAddLeave(true)}
        >
          <span className="add-leave-icon">+</span>
          <span>Add Leave</span>
        </button>
      </div>

      {/* Scheduled Leave List */}
      <div className="scheduled-leave-section">
        <h2 className="scheduled-leave-title">
          Scheduled Leave for {selectedDate.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </h2>
        {slotsForSelectedDate.length === 0 ? (
          <div className="no-leave-message">No leave scheduled for this date.</div>
        ) : (
          <div className="leave-slots-list">
            {slotsForSelectedDate.map((slot) => (
              <div
                key={slot.id}
                className="leave-slot-card leave-slot-card-clickable"
                onClick={() => setSelectedLeaveSlot(slot)}
              >
                <div className="leave-slot-header">
                  <div className="leave-slot-time">
                    {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                  </div>
                  <div className="leave-slot-header-right">
                    <div className={`leave-slot-status leave-slot-status-${slot.status}`}>
                      {slot.status}
                    </div>
                    <div className="leave-slot-actions">
                      <button
                        type="button"
                        className="leave-slot-action-button leave-slot-edit-button"
                        onClick={(e) => handleEditLeave(slot, e)}
                        title="Edit leave"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        className="leave-slot-action-button leave-slot-delete-button"
                        onClick={(e) => handleDeleteLeave(slot, e)}
                        title="Delete leave"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
                <div className="leave-slot-details">
                  <div className="leave-slot-detail-row">
                    <span className="leave-slot-label">Type:</span>
                    <span className="leave-slot-value">{formatLeaveType(slot.leave_type)}</span>
                  </div>
                  {slot.reason && (
                    <div className="leave-slot-detail-row">
                      <span className="leave-slot-label">Reason:</span>
                      <span className="leave-slot-value">{slot.reason}</span>
                    </div>
                  )}
                  <div className="leave-slot-detail-row">
                    <span className="leave-slot-label">Scope:</span>
                    <span className="leave-slot-value">{formatLeaveScope(slot.leave_scope)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showingAddLeave && (
        <div className="modal-overlay" onClick={() => setShowingAddLeave(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <AddLeaveForm
              selectedDate={selectedDate}
              practices={practices}
              onClose={() => setShowingAddLeave(false)}
              onSuccess={() => {
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
            />
          </div>
        </div>
      )}

      {selectedLeaveSlot && (
        <div className="modal-overlay" onClick={() => setSelectedLeaveSlot(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <LeaveDetailsView
              leave={selectedLeaveSlot}
              practices={practices}
              onClose={() => setSelectedLeaveSlot(null)}
              onUpdate={() => {
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
              onDelete={() => {
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
            />
          </div>
        </div>
      )}

      {editingLeaveSlot && (
        <div className="modal-overlay" onClick={() => setEditingLeaveSlot(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <EditLeaveForm
              leave={editingLeaveSlot}
              practices={practices}
              onClose={() => setEditingLeaveSlot(null)}
              onSuccess={() => {
                setEditingLeaveSlot(null);
                loadLeaveSlots();
                loadAppointmentCountsForMonth(displayedMonth);
              }}
            />
          </div>
        </div>
      )}

      {showingDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowingDeleteConfirm(false)}>
          <div className="modal-content-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Leave</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this leave? This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="modal-button-secondary"
                onClick={() => {
                  setShowingDeleteConfirm(false);
                  setDeletingLeaveSlotId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="modal-button-danger"
                onClick={confirmDeleteLeave}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditAvailabilityContent;

